import { Api } from '@/api/api.client';
import type { Components } from '@/api/generated/api';
import { useDisplayName } from '@/composables/useDisplayName';
import { useQueryWithToast } from '@/composables/useQueryWithToast';
import { useToast } from '@/composables/useToast';
import { IUserWithPermissions } from '@/interfaces';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { FilterOperationType, IFilter } from '@chax-at/prisma-filter-common';
import { useQueryClient } from '@tanstack/vue-query';
import { IAccessRightFilter } from 'common/interface/filter.interface';
import { hasEqualPermissionsAndSubjectAccess } from 'common/utils/access.right.utils';
import { runBulk } from 'common/utils/function.utils';
import { computed, ComputedRef } from 'vue';
import { useSchoolSubjectsQuery } from './schoolSubject/schoolSubject.queries';
import { useSchoolSubjectGroupsQuery } from './schoolSubjectGroups/schoolSubjectGroup.queries';
import { getOrderedSchoolSubjects } from 'common/utils/school.subject.utils';
import { usePermissions } from './usePermissions';

interface ISchoolClassTeamComposable {
  accessRights: ComputedRef<Components.Schemas.SchoolClassAccessRightDto[]>;
  isLoading: ComputedRef<boolean>;
  upsertAccessRights: (
    schoolClassId: number,
    addAccessRightsDto: Components.Schemas.AddAccessRightDto[],
    showToast?: boolean,
  ) => Promise<void>;
  removeAccessRights: (
    accessRights: Components.Schemas.SchoolClassAccessRightDto[],
    showToast?: boolean,
  ) => Promise<void>;
  getHasUserChanges: (usersWithAccess: IUserWithPermissions[]) => boolean;
  getUserWithPermissions: (ar: Components.Schemas.SchoolClassAccessRightDto) => IUserWithPermissions;
  userWithPermissions: ComputedRef<IUserWithPermissions[]>;
  refresh: () => Promise<void>;
}

export function useSchoolClassTeam(): ISchoolClassTeamComposable {
  const { catchError, editSuccessToast, createSuccessToast } = useToast();
  const { currentClass } = useCurrentClass();
  const subjectsQuery = useSchoolSubjectsQuery(() => currentClass.value?.id);
  const groupsQuery = useSchoolSubjectGroupsQuery(() => currentClass.value?.id);
  const allSchoolSubjects = computed(() =>
    getOrderedSchoolSubjects(subjectsQuery.data.value ?? [], groupsQuery.data.value ?? []),
  );
  const { canManageAccessRights, hasTenantOwnerOrAdminAccess } = usePermissions();
  const { nameSortation } = useDisplayName();

  const canModifyAccessRights = computed(() => canManageAccessRights.value || hasTenantOwnerOrAdminAccess.value);

  const getAccessRightsForCurrentClass = async (): Promise<Components.Schemas.SchoolClassAccessRightDto[]> => {
    // the backend orders by the user's nameSortation setting
    const filter: IFilter<IAccessRightFilter> = {
      filter: [
        {
          field: 'schoolClassId',
          type: FilterOperationType.Eq,
          value: currentClass.value?.id,
        },
      ],
    };
    const result = await Api.Routes.SchoolClass.getAccessRights({ $filter: filter });
    return result.rows;
  };

  const accessRightsQueryKey = computed(() => ['accessRights', 'list', currentClass.value?.id, nameSortation.value]);
  const queryClient = useQueryClient();
  const query = useQueryWithToast({
    queryKey: accessRightsQueryKey,
    queryFn: getAccessRightsForCurrentClass,
  });
  const isLoading = computed(() => query.isLoading.value || query.isFetching.value);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: accessRightsQueryKey });
  };

  const upsertAccessRights = async (
    schoolClassId: number,
    addAccessRightsDto: Components.Schemas.AddAccessRightDto[],
    showToast: boolean = true,
  ): Promise<void> => {
    if (!canModifyAccessRights.value) return;
    const dto: Components.Schemas.UpsertSchoolClassAccessDto = {
      accessRights: addAccessRightsDto,
    };
    await catchError(async () => {
      await Api.Routes.SchoolClass.upsertAccessRights({ schoolClassId }, dto);
      await refresh();
      if (showToast) createSuccessToast();
    });
  };

  const removeAccessRights = async (
    accessRights: Components.Schemas.SchoolClassAccessRightDto[],
    showToast: boolean = true,
  ): Promise<void> => {
    if (!accessRights.length) return;

    await catchError(async () => {
      await runBulk(
        accessRights,
        async (ar) => {
          await Api.Routes.SchoolClass.removeAccessRights({
            schoolClassId: ar.schoolClass.id,
            accessRightId: ar.id,
          });
        },
        { concurrency: 8 },
      );

      await refresh();
      if (showToast) editSuccessToast();
    });
  };

  const accessRights = computed<Components.Schemas.SchoolClassAccessRightDto[]>(() => {
    return query.data.value ?? [];
  });

  const getUserWithPermissions = (ar: Components.Schemas.SchoolClassAccessRightDto): IUserWithPermissions => {
    const subjectIds = ar.permissions.subjects?.map((subj) => subj.id) || [];
    return {
      ...ar,
      schoolSubjects: allSchoolSubjects.value.filter((sS) => subjectIds.includes(sS.id)),
    };
  };

  const getHasUserChanges = (usersWithAccess: IUserWithPermissions[]): boolean => {
    const currentUserIdsWithAccess = accessRights.value.map((ar) => ar.user.id);
    const newUserIdsWithAccess = usersWithAccess.map((u) => u.user.id);

    const originalAccessRights = accessRights.value.map((ar) => getUserWithPermissions(ar));
    for (const oAR of originalAccessRights) {
      const nAR = usersWithAccess.find((u) => u.user.id === oAR.user.id);
      if (!nAR) continue;
      if (!hasEqualPermissionsAndSubjectAccess(oAR, nAR)) {
        return true;
      }
    }

    return (
      currentUserIdsWithAccess.length !== newUserIdsWithAccess.length ||
      currentUserIdsWithAccess.some((id) => !newUserIdsWithAccess.includes(id)) ||
      newUserIdsWithAccess.some((id) => !currentUserIdsWithAccess.includes(id))
    );
  };

  const userWithPermissions = computed<IUserWithPermissions[]>(() => {
    return accessRights.value.map((ar) => getUserWithPermissions(ar));
  });

  return {
    accessRights,
    isLoading,
    removeAccessRights,
    upsertAccessRights,
    getHasUserChanges,
    getUserWithPermissions,
    userWithPermissions,
    refresh,
  };
}
